import { NextPageContext } from 'next';
import { Frame, ScoutPayload } from '@/types/location';
import { apiCall } from '@/utils/api';
import { getHereApiKey } from '@/utils/map';
import { buildQueryParamString } from '@/utils/url';

export const registerLocations = async (
  locations: ScoutPayload[],
  currentOrganization: string | null,
) => {
  try {
    const response = await apiCall(
      `${process.env.NEXT_PUBLIC_API}/loi/register`,
      {
        method: 'POST',
      },
      { locations, organizationId: currentOrganization },
    );
    return response;
  } catch (error: any) {
    return { error };
  }
};

export const deregisterLocations = async (
  ids: string[],
  organizationId?: string | null,
) => {
  try {
    const response = await apiCall(
      `${process.env.NEXT_PUBLIC_API}/loi/deregister`,
      {
        method: 'POST',
      },
      { ids, organizationId },
    );
    return response;
  } catch (error: any) {
    return { error };
  }
};

export const updateLocation = async (
  id: string,
  { name }: Pick<ScoutPayload, 'name'>,
) => {
  try {
    return await apiCall(
      `${process.env.NEXT_PUBLIC_API}/loi/edit/${id}`,
      { method: 'PUT' },
      { name },
    );
  } catch (error: unknown) {
    return { error };
  }
};

export const optimisticallyUpdateLastViewed = async (
  id: string,
  organizationId?: string | null,
) => {
  try {
    await apiCall(
      `${process.env.NEXT_PUBLIC_API}/loi/lastViewed`,
      {
        method: 'POST',
      },
      { id, organizationId },
    );
  } catch (error) {
    console.log(error);
  }
};

export const addToDemo = async (locationId: string, jobId: string) => {
  try {
    await apiCall(
      `${process.env.NEXT_PUBLIC_API}/loi/addToDemo`,
      {
        method: 'POST',
      },
      { locationId, jobId },
    );
  } catch (error) {
    console.log(error);
  }
};

export const createOrganization = async () => {
  try {
    const response = await apiCall(
      `${process.env.NEXT_PUBLIC_API}/organization/create`,
      {
        method: 'POST',
      },
    );

    return response;
  } catch (error: any) {
    return { error };
  }
};

export const getPointFromAddress = async (address: string) => {
  try {
    const queryParams = buildQueryParamString({
      q: encodeURI(address),
      at: '0,0',
      apiKey: getHereApiKey(),
    });
    const url = `https://autosuggest.search.hereapi.com/v1/autosuggest${queryParams}`;

    const response = await apiCall(url, {
      method: 'GET',
      credentials: 'omit',
    });

    return response;
  } catch (error: any) {
    return { error };
  }
};

export const getMonitoredLocations = async (
  currentOrganization: string | null,
  req?: NextPageContext['req'],
) => {
  try {
    const organizationIdQuery = currentOrganization
      ? `?organizationId=${currentOrganization}`
      : '';

    const response = await apiCall(
      `${process.env.NEXT_PUBLIC_API}/loi${organizationIdQuery}`,
      {
        method: 'GET',
        headers: {
          cookie: req?.headers.cookie,
        },
      },
    );
    return response;
  } catch (error: any) {
    return { error };
  }
};

export const getDemoLocations = async () => {
  try {
    const response = await apiCall(`${process.env.NEXT_PUBLIC_API}/loi/demo`, {
      method: 'GET',
    });
    return response;
  } catch (error: any) {
    return { error };
  }
};

export const getImagesForDemoLocation = async (
  locationId: string,
  signal?: AbortSignal,
): Promise<
  | {
      frames: Frame[];
      cost: number;
      creditsRemaining: number;
      notEnoughCredits: boolean;
    }
  | { error: any }
> => {
  try {
    const response = await apiCall(
      `${process.env.NEXT_PUBLIC_API}/loi/demo/imagery/${locationId}`,
      {
        method: 'GET',
        signal,
      },
    );

    return response;
  } catch (error: any) {
    return { error };
  }
};
